// pages/OneCReport.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getProducts } from '../services/productService';
import { getCategories } from '../services/categoryService';
import api from '../services/api';

/**
 * Страница отчета для выгрузки в 1С
 */
const OneCReport = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useOldPriceTags, setUseOldPriceTags] = useState(true);
  const [warnings, setWarnings] = useState([]);

  // Загрузка данных
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Фильтрация товаров
  useEffect(() => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.barcode?.includes(searchTerm)
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(p => p.categoryId === parseInt(categoryFilter, 10));
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, categoryFilter]);

  // Обновление выбранных товаров при изменении selectAll
  useEffect(() => {
    if (selectAll) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    }
  }, [selectAll, filteredProducts]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts({ pageSize: 1000 }, true);
      const productsArray = Array.isArray(data) ? data : (data.items || data.data || []);
      setProducts(productsArray.filter(p => p.isActive));
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error);
      toast.error('Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getCategories({}, true);
      const categoriesArray = Array.isArray(data) ? data : (data.items || data.data || []);
      setCategories(categoriesArray.filter(c => c.isActive));
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
    setSelectAll(!selectAll);
  };

  const handleGenerateReport = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Выберите хотя бы один товар');
      return;
    }

    // Проверка дат
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error('Начальная дата не может быть позже конечной');
      return;
    }

    setGenerating(true);
    setWarnings([]);
    try {
      const requestData = {
        productIds: selectedProducts,
        useOldPriceTags: useOldPriceTags
      };

      if (startDate) {
        requestData.startDate = new Date(startDate + 'T00:00:00').toISOString();
      }
      if (endDate) {
        requestData.endDate = new Date(endDate + 'T23:59:59').toISOString();
      }

      const response = await api.post(
        '/reports/1c/generate',
        requestData,
        { responseType: 'blob' }
      );

      // Проверяем заголовки для получения предупреждений
      // Axios приводит заголовки к нижнему регистру
      const warningsHeader = response.headers['x-warnings'] || response.headers['X-Warnings'];
      if (warningsHeader) {
        try {
          const decoded = decodeURIComponent(warningsHeader);
          const warningsData = JSON.parse(decoded);
          setWarnings(warningsData);
          if (warningsData.length > 0) {
            toast.warning(`Использованы старые ценники для ${warningsData.length} товаров`, {
              autoClose: 5000
            });
          }
        } catch (e) {
          console.error('Ошибка парсинга предупреждений:', e);
          console.error('Заголовок:', warningsHeader);
        }
      }

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `1C_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Отчет успешно сгенерирован');
    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
      toast.error(error.response?.data?.message || 'Не удалось сгенерировать отчет');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Отчет для выгрузки в 1С</h1>
        <p className="text-gray-600">Генерация отчета со средними ценами для интеграции с 1С</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Форма выбора товаров */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Выбор товаров</h2>

            {/* Фильтры */}
            <div className="mb-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Поиск товара
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Название или штрихкод..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Категория
                </label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Все категории</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Чекбокс "Выбрать все" */}
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                id="selectAll"
                checked={selectAll && filteredProducts.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="selectAll" className="ml-2 text-sm font-medium text-gray-700">
                Выбрать все ({filteredProducts.length})
              </label>
            </div>

            {/* Список товаров */}
            <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Загрузка товаров...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Товары не найдены
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProducts.map(product => (
                    <div
                      key={product.id}
                      className="p-3 hover:bg-gray-50 flex items-center"
                    >
                      <input
                        type="checkbox"
                        id={`product-${product.id}`}
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleProductToggle(product.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`product-${product.id}`}
                        className="ml-3 flex-1 cursor-pointer"
                      >
                        <div className="font-medium text-gray-900">{product.name}</div>
                        {product.barcode && (
                          <div className="text-sm text-gray-500">Штрихкод: {product.barcode}</div>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Панель действий */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Параметры отчета</h2>

            <div className="space-y-4">
              {/* Фильтр по датам */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Период данных
                </label>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Начальная дата</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Конечная дата</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Опция использования старых ценников */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="useOldPriceTags"
                  checked={useOldPriceTags}
                  onChange={(e) => setUseOldPriceTags(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                />
                <label htmlFor="useOldPriceTags" className="ml-2 text-sm text-gray-700">
                  Использовать старые ценники, если за указанный период данных нет
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Выбрано товаров:</h3>
                <p className="text-2xl font-bold text-blue-600">{selectedProducts.length}</p>
              </div>

              <button
                onClick={handleGenerateReport}
                disabled={generating || selectedProducts.length === 0}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generating ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Генерация...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Сгенерировать отчет
                  </>
                )}
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Формат отчета:</h3>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Для каждого товара выбираются 3 минимальные и 3 максимальные цены</li>
                  <li>• Для каждой цены указывается: цена (руб), вес/объем, цена за единицу (руб/кг или руб/л)</li>
                  <li>• Рассчитывается средняя цена по минимальным и максимальным значениям</li>
                  <li>• Отчет в формате Excel (.xlsx)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Предупреждения о старых ценниках */}
      {warnings.length > 0 && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-yellow-600 mt-0.5 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                Внимание: Использованы старые ценники
              </h3>
              <p className="text-sm text-yellow-800 mb-3">
                Для следующих товаров не найдено ценников за указанный период. Использованы старые ценники:
              </p>
              <ul className="text-sm text-yellow-800 space-y-1 max-h-40 overflow-y-auto">
                {warnings.map((warning, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>
                      <strong>{warning.productName}</strong>
                      {warning.oldestDate && (
                        <span className="text-yellow-700">
                          {' '}(самый старый ценник от {new Date(warning.oldestDate).toLocaleDateString('ru-RU')})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OneCReport;
